function devOriginUrl(path: string) {
  if (typeof window === "undefined") return undefined;
  return new URL(path, window.location.origin).href.replace(/\/$/, "");
}

function devWsUrl() {
  if (typeof window === "undefined") return undefined;
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

/** Turn /api-style dev env values into absolute URLs for stoat-api's URL constructor. */
function resolveDevUrl(envValue: string | undefined, fallbackPath: string) {
  if (envValue?.startsWith("http://") || envValue?.startsWith("https://")) {
    return envValue;
  }
  const path = envValue?.startsWith("/") ? envValue : fallbackPath;
  return devOriginUrl(path);
}

const useDevProxy = import.meta.env.DEV && !!import.meta.env.VITE_DEV_API_URL;

const DEFAULT_API_URL =
  (useDevProxy
    ? resolveDevUrl(import.meta.env.VITE_DEV_API_URL as string, "/api")
    : undefined) ??
  (import.meta.env.VITE_API_URL as string) ??
  "https://stoat.chat/api";
export default {
  /**
   * Whether to emit additional debug information
   */
  DEBUG: import.meta.env.DEV || true,
  /**
   * What API server to connect to by default.
   */
  DEFAULT_API_URL,
  /**
   * Official public instance features (Discover, Lounge, etc.)
   */
  IS_STOAT: false,
  /**
   * Server/community features (multi-channel servers, server list, etc.)
   * Opt in with VITE_CFG_ENABLE_SERVERS=true (production builds set this via write-client-env.sh).
   */
  ENABLE_SERVERS:
    (
      (import.meta.env.VITE_CFG_ENABLE_SERVERS as string | undefined) ?? "false"
    ).toLowerCase() !== "false",
  /**
   * What WS server to connect to by default.
   */
  DEFAULT_WS_URL:
    (useDevProxy
      ? (import.meta.env.VITE_DEV_WS_URL as string | undefined)?.startsWith("ws")
        ? (import.meta.env.VITE_DEV_WS_URL as string)
        : devWsUrl()
      : undefined) ??
    (import.meta.env.VITE_WS_URL as string) ??
    "wss://stoat.chat/events",
  /**
   * What media server to connect to by default.
   */
  DEFAULT_MEDIA_URL:
    (useDevProxy
      ? resolveDevUrl(import.meta.env.VITE_DEV_MEDIA_URL as string, "/autumn")
      : undefined) ??
    (import.meta.env.VITE_MEDIA_URL as string) ??
    "https://cdn.stoatusercontent.com",
  /**
   * What proxy server to connect to by default.
   */
  DEFAULT_PROXY_URL:
    (useDevProxy
      ? resolveDevUrl(import.meta.env.VITE_DEV_PROXY_URL as string, "/january")
      : undefined) ??
    (import.meta.env.VITE_PROXY_URL as string) ??
    "https://proxy.stoatusercontent.com",
  /**
   * hCaptcha site key to use if enabled
   */
  HCAPTCHA_SITEKEY: import.meta.env.VITE_HCAPTCHA_SITEKEY as string,
  /**
   * Maximum number of replies a message can have
   */
  MAX_REPLIES: (import.meta.env.VITE_CFG_MAX_REPLIES as number) ?? 5,
  /**
   * Maximum number of attachments a message can have
   */
  MAX_ATTACHMENTS: (import.meta.env.VITE_CFG_MAX_ATTACHMENTS as number) ?? 5,
  /**
   * Max file size allowed for uploads (in bytes). 100 MB for private Renk8Rooms.
   */
  MAX_FILE_SIZE:
    (import.meta.env.VITE_CFG_MAX_FILE_SIZE as number) ?? 100_000_000,
  /**
   * RNNoise worklet CDN host location. Defaults to blank, which uses the url provided by the livekit-rnnoise-processor package.
   */
  RNNOISE_WORKLET_CDN_URL:
    (import.meta.env.VITE_RNNOISE_WORKLET_CDN_URL as string) ?? "",
  /**
   * Enable video and screensharing in voice calls (opt out with VITE_CFG_ENABLE_VIDEO=false).
   */
  ENABLE_VIDEO:
    (
      (import.meta.env.VITE_CFG_ENABLE_VIDEO as string | undefined) ?? "true"
    ).toLowerCase() !== "false",
  /**
   * Session ID to set during development.
   */
  DEVELOPMENT_SESSION_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_SESSION_ID as string)
    : undefined,
  /**
   * Token to set during development.
   */
  DEVELOPMENT_TOKEN: import.meta.env.DEV
    ? (import.meta.env.VITE_TOKEN as string)
    : undefined,
  /**
   * User ID to set during development.
   */
  DEVELOPMENT_USER_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_USER_ID as string)
    : undefined,
};
